/**
 * RFQ quote analysis — the derivations the vendor-bidding service does not do.
 *
 * Line money (line_total, total_net_amount) is settled on the service. Freight,
 * insurance and packing live on the quote header / payload. Staff totals must
 * match the vendor portal: line net + those charges.
 */
import { parseMoney, daysUntilExpiry } from '../utils';

/** Quotes in these states are not competing and must never take a rank. */
const NON_COMPETING_STATUSES = ['DRAFT', 'WITHDRAWN'];

/**
 * Money that a sealed RFQ has withheld. The service sends null rather than a
 * number, and null must never render as ₹0.00 — that reads as "this vendor
 * quoted nothing", which is a different and wrong statement.
 */
export const SEALED_PLACEHOLDER = '•••••';

export function formatMaybeSealed(value, formatter) {
  return value == null ? SEALED_PLACEHOLDER : formatter(value);
}

/** 4 dp, half-up — the precision the service rounds every line amount to. */
export function r4(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round((v + Number.EPSILON) * 10000) / 10000;
}

/**
 * One line's money, mirroring the service's calculate_line_amounts exactly so
 * a quote form can preview totals that match what gets stored.
 */
export function calcLine({ qty, rate, discountPercent = 0, taxPercent = 0 }) {
  const q = Number(qty) || 0;
  const basic = r4(q * (Number(rate) || 0));
  const discount = r4((basic * (Number(discountPercent) || 0)) / 100);
  const taxable = basic - discount;
  const tax = r4((taxable * (Number(taxPercent) || 0)) / 100);
  const total = r4(taxable + tax);
  return {
    basic,
    discount,
    taxable,
    tax,
    total,
    netUnitRate: q > 0 ? r4(taxable / q) : 0,
  };
}

/** Quote-level totals from priced lines. Skipped lines are simply absent. */
export function calcQuoteTotals(lines = []) {
  const sum = (pick) => r4(lines.reduce((acc, l) => acc + pick(l), 0));
  return {
    basic: sum((l) => l.basic),
    discount: sum((l) => l.discount),
    taxable: sum((l) => l.taxable),
    tax: sum((l) => l.tax),
    net: sum((l) => l.total),
  };
}

export function getQuoteId(q) {
  return q?.vendor_quote_id || q?.id || q?.quote_id || null;
}

export function otherChargesAmount(q) {
  if (!q || typeof q !== 'object') return 0;
  const payload = q.quote_payload && typeof q.quote_payload === 'object' ? q.quote_payload : {};
  return r4(
    parseMoney(q.freight ?? payload.freight)
    + parseMoney(q.insurance ?? payload.insurance)
    + parseMoney(q.packing ?? payload.packing ?? q.other_charges ?? payload.other_charges)
  );
}

/** Sum of visible comparison line totals for one vendor quote. */
export function quotedLineNet(comparison, quoteId) {
  if (!comparison || comparison.rates_visible === false || !quoteId) return null;
  let sum = 0;
  let found = false;
  (comparison.items || []).forEach((row) => {
    (row.quotes || []).forEach((line) => {
      if (String(line.vendor_quote_id || '') === String(quoteId)) {
        sum += parseMoney(line.line_total);
        found = true;
      }
    });
  });
  return found ? r4(sum) : null;
}

/**
 * All-in net the vendor portal shows: priced lines + freight/insurance/packing.
 * Prefer live comparison lines when the stored header total is stale.
 */
export function quoteAllInNet(q, comparison) {
  if (!q) return null;
  const fromLines = quotedLineNet(comparison, getQuoteId(q));
  const charges = otherChargesAmount(q);
  if (fromLines != null) return r4(fromLines + charges);
  if (q.total_net_amount == null && q.total_value == null) return null;
  return parseMoney(q.total_net_amount ?? q.total_value);
}

/** vendor_quote_id -> how many RFQ items that quote actually priced. */
export function quotedCounts(comparison) {
  const counts = {};
  (comparison?.items || []).forEach((row) => {
    (row.quotes || []).forEach((line) => {
      const id = line.vendor_quote_id;
      if (id) counts[id] = (counts[id] || 0) + 1;
    });
  });
  return counts;
}

/**
 * PART 4.4 — rank quotes, complete ones first.
 *
 * A quote that skips items is not comparable on net total: leaving items out
 * makes it look cheaper. So every complete quote outranks every partial one
 * regardless of price, and partials are flagged so the UI can say why.
 */
export function rankQuotes(comparison) {
  const itemCount = (comparison?.items || []).length;
  const counts = quotedCounts(comparison);

  const competing = (comparison?.vendor_quotes || []).filter(
    (q) => !NON_COMPETING_STATUSES.includes(q.status),
  );

  const rows = competing.map((q) => {
    const id = getQuoteId(q);
    const quotedCount = counts[id] || 0;
    return {
      ...q,
      quoteId: id,
      net: quoteAllInNet(q, comparison) ?? parseMoney(q.total_net_amount),
      quotedCount,
      itemCount,
      partial: itemCount > 0 && quotedCount < itemCount,
      // 0 = complete, 1 = partial, 2 = priced nothing at all
      tier: quotedCount === 0 ? 2 : (quotedCount < itemCount ? 1 : 0),
    };
  });

  rows.sort((a, b) => (a.tier - b.tier) || (a.net - b.net));
  return rows.map((r, i) => ({ ...r, rank: i + 1, rankLabel: `L${i + 1}` }));
}

/**
 * The cheapest net that is actually comparable — i.e. among complete quotes.
 * With no complete quote, falls back to the cheapest of whatever exists so the
 * commercial score still has a baseline. Returns null when there is nothing.
 */
export function lowestComparableNet(ranked) {
  const complete = (ranked || []).filter((q) => q.tier === 0);
  const pool = complete.length ? complete : (ranked || []).filter((q) => q.tier < 2);
  if (!pool.length) return null;
  return pool.reduce((min, q) => (q.net < min ? q.net : min), pool[0].net);
}

/** PART 4.5 — the cheapest line for each RFQ item. */
export function perItemLowest(comparison) {
  const out = {};
  (comparison?.items || []).forEach((row) => {
    const lines = row.quotes || [];
    if (!lines.length) return;
    const best = lines.reduce((lo, line) => (
      parseMoney(line.line_total) < parseMoney(lo.line_total) ? line : lo
    ), lines[0]);
    out[row.rfq_item_id] = best;
  });
  return out;
}

/**
 * PART 4.6 — what awarding item by item would cost against a single L1 award.
 *
 * hasCompleteQuote false means no vendor priced every item, so there is no
 * honest single-award figure to compare the split against.
 */
export function splitAwardSummary(comparison, ranked) {
  const lowest = perItemLowest(comparison);
  const lineSplit = Object.values(lowest)
    .reduce((sum, line) => sum + parseMoney(line.line_total), 0);

  const rankedByVendor = {};
  (ranked || []).forEach((q) => {
    if (q.vendor_id) rankedByVendor[q.vendor_id] = q;
  });

  const byVendor = {};
  Object.entries(lowest).forEach(([rfqItemId, line]) => {
    const key = line.vendor_id;
    if (!byVendor[key]) byVendor[key] = { vendorId: key, items: [], value: 0 };
    byVendor[key].items.push(rfqItemId);
    byVendor[key].value += parseMoney(line.line_total);
  });
  Object.values(byVendor).forEach((v) => {
    v.value = r4(v.value + otherChargesAmount(rankedByVendor[v.vendorId] || {}));
  });

  const splitCharges = Object.keys(byVendor).reduce((sum, vid) => (
    sum + otherChargesAmount(rankedByVendor[vid] || {})
  ), 0);
  const splitTotal = r4(lineSplit + splitCharges);

  const hasCompleteQuote = (ranked || []).some((q) => q.tier === 0);
  const lowestSingle = lowestComparableNet(ranked);
  const saving = lowestSingle == null ? null : lowestSingle - splitTotal;

  return {
    perItemLowest: lowest,
    splitTotal,
    lowestSingle,
    saving,
    savingPct: saving != null && lowestSingle ? (saving / lowestSingle) * 100 : null,
    hasCompleteQuote,
    byVendor: Object.values(byVendor),
    itemsCovered: Object.keys(lowest).length,
    itemCount: (comparison?.items || []).length,
  };
}

/**
 * PART 4.7 — commercial score is price-derived, never typed by hand.
 * The cheapest comparable quote scores 100; everything else scores in
 * proportion to how much dearer it is.
 */
export function commercialScoreFor(net, lowestNet) {
  const n = parseMoney(net);
  if (!n || lowestNet == null) return null;
  return (lowestNet / n) * 100;
}

/**
 * Weighted total. Weights are given as percentages that sum to 100 (the UI
 * shows them that way); the service stores them as fractions.
 */
export function weightedTotal(technical, commercial, techWeight, commWeight) {
  const t = Number(technical);
  const c = Number(commercial);
  if (!Number.isFinite(t) || !Number.isFinite(c)) return null;
  return (t * techWeight + c * commWeight) / 100;
}

/** PART 4.8 — who has to sign off, by award value. */
export function approvalRoute(value) {
  const v = parseMoney(value);
  if (v < 100000) return ['Purchase Manager'];
  if (v < 1000000) return ['Purchase Manager', 'HOD'];
  if (v < 5000000) return ['Purchase Manager', 'HOD', 'Finance Head'];
  return ['Purchase Manager', 'HOD', 'Finance Head', 'Director'];
}

function isDrugItem(item) {
  return Boolean(item?.is_drug_category ?? item?.is_drug ?? item?.is_controlled_drug ?? item?.is_narcotic);
}

/**
 * PART 7.6 — every reason an award cannot go ahead yet.
 *
 * Returns one entry per unmet condition, each saying what is wrong and what
 * would clear it. An empty array means the award button is safe to enable.
 */
export function awardBlockers({
  quote,
  ranked = [],
  rfqItems = [],
  vendor,
  minimumQuotes,
  justification = '',
  nonL1Reason = '',
} = {}) {
  const blockers = [];
  if (!quote) return [{ key: 'no-quote', message: 'Select a quote to award.' }];

  // Without ranking there is nothing to check rank or completeness against, so
  // fail closed rather than waving the award through unguarded.
  if (!ranked.length) {
    return [{
      key: 'no-ranking',
      message: 'Quote comparison has not loaded, so this award cannot be checked yet. Open the Comparison tab and try again.',
    }];
  }

  const target = ranked.find((q) => q.quoteId === getQuoteId(quote)) || quote;

  // 2 — vendor must be an approved vendor, not provisional
  if (vendor && vendor.approval_status !== 'APPROVED') {
    blockers.push({
      key: 'provisional-vendor',
      message: 'This vendor is not approved yet. Approve the vendor before awarding.',
    });
  }

  // 3 — expired drug licence against an RFQ that contains drug items
  const hasDrugItem = (rfqItems || []).some(isDrugItem);
  if (hasDrugItem && vendor) {
    const daysLeft = daysUntilExpiry(vendor.license_valid_till);
    if (!vendor.license_valid_till) {
      blockers.push({
        key: 'licence-missing',
        message: 'This RFQ has drug items and the vendor has no drug licence on record. Add a valid licence first.',
      });
    } else if (daysLeft != null && daysLeft < 0) {
      blockers.push({
        key: 'licence-expired',
        message: `This RFQ has drug items and the vendor's drug licence expired ${Math.abs(daysLeft)} days ago. Renew it before awarding.`,
      });
    }
  }

  // 4 — below the RFQ's own minimum quote count needs a written reason
  const min = Number(minimumQuotes);
  if (Number.isFinite(min) && min > 0 && ranked.length < min) {
    if (justification.trim().length < 15) {
      blockers.push({
        key: 'below-minimum',
        message: `Only ${ranked.length} of the required ${min} quotes were received. Write at least 15 characters of justification.`,
      });
    }
  }

  // 5 — awarding anyone but L1 needs a reason
  if (target.rank && target.rank > 1 && !nonL1Reason) {
    blockers.push({
      key: 'non-l1',
      message: `This is ${target.rankLabel}, not the lowest quote. Choose a reason for not awarding L1.`,
    });
  }

  // 6 — a partial quote cannot take a whole-RFQ award
  if (target.partial) {
    blockers.push({
      key: 'partial-quote',
      message: `This quote prices only ${target.quotedCount} of ${target.itemCount} items, so its total is not a full-RFQ price. It cannot take a full award.`,
    });
  }

  return blockers;
}

/** Requested qty on an RFQ comparison row. */
export function requestedQty(row) {
  return Number(row?.requested_quantity) || 0;
}

/** Qty that vendor actually quoted; falls back to the RFQ requested qty. */
export function quotedQtyForLine(row, line) {
  const quoted = Number(line?.quoted_quantity);
  if (Number.isFinite(quoted) && quoted > 0) return quoted;
  return requestedQty(row);
}

export function parseAwardQty(value) {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function awardedQtyOnItem(assignmentForItem = {}) {
  return r4(Object.values(assignmentForItem).reduce((sum, v) => sum + parseAwardQty(v), 0));
}

export function remainingQtyOnItem(row, assignmentForItem) {
  return r4(Math.max(0, requestedQty(row) - awardedQtyOnItem(assignmentForItem)));
}

/**
 * Default split: full requested qty to the lowest bidder on each item, 0 to
 * every other vendor who priced that line.
 */
export function defaultSplitQtyAssignment(comparison) {
  const lowest = perItemLowest(comparison);
  const out = {};
  (comparison?.items || []).forEach((row) => {
    const requested = requestedQty(row);
    const byQuote = {};
    (row.quotes || []).forEach((line) => {
      const id = line.vendor_quote_id;
      if (!id) return;
      const cap = quotedQtyForLine(row, line);
      byQuote[id] = lowest[row.rfq_item_id]?.vendor_quote_id === id
        ? Math.min(requested, cap)
        : 0;
    });
    out[row.rfq_item_id] = byQuote;
  });
  return out;
}

/** Line money for an awarded slice of a quoted line. */
export function awardedLineValue(line, awardedQty, row) {
  const qty = parseAwardQty(awardedQty);
  if (qty <= 0) return 0;
  const rate = Number(line?.unit_rate);
  if (Number.isFinite(rate) && rate > 0) {
    return calcLine({
      qty,
      rate,
      discountPercent: line.discount_percent,
      taxPercent: line.tax_percent,
    }).total;
  }
  const quotedQty = quotedQtyForLine(row, line);
  const total = parseMoney(line?.line_total);
  if (quotedQty > 0) return r4(total * (qty / quotedQty));
  return total;
}

/**
 * Group a qty assignment by winning vendor quote. Other charges are added
 * once per vendor who receives any quantity.
 */
export function splitAwardGroupsFromQty(comparison, assignment, ranked) {
  const quoteById = {};
  (ranked || []).forEach((q) => { quoteById[q.quoteId] = q; });
  const out = {};
  (comparison?.items || []).forEach((row) => {
    const byQuote = assignment?.[row.rfq_item_id] || {};
    (row.quotes || []).forEach((line) => {
      const qty = parseAwardQty(byQuote[line.vendor_quote_id]);
      if (qty <= 0) return;
      const qid = line.vendor_quote_id;
      if (!out[qid]) out[qid] = { quoteId: qid, lines: [], value: 0 };
      const value = awardedLineValue(line, qty, row);
      out[qid].lines.push({
        rfq_item_id: row.rfq_item_id,
        awarded_quantity: qty,
        value,
      });
      out[qid].value += value;
    });
  });
  Object.values(out).forEach((g) => {
    g.value = r4(g.value + otherChargesAmount(quoteById[g.quoteId] || {}));
  });
  return Object.values(out);
}

export function splitQtyIssues(comparison, assignment) {
  const overRequested = [];
  const overQuoted = [];
  let awardedCount = 0;
  (comparison?.items || []).forEach((row) => {
    const byQuote = assignment?.[row.rfq_item_id] || {};
    const requested = requestedQty(row);
    let allocated = 0;
    (row.quotes || []).forEach((line) => {
      const qty = parseAwardQty(byQuote[line.vendor_quote_id]);
      if (qty <= 0) return;
      awardedCount += 1;
      allocated += qty;
      const cap = quotedQtyForLine(row, line);
      if (qty > cap + 1e-9) overQuoted.push(row);
    });
    if (allocated > requested + 1e-9) overRequested.push(row);
  });
  return { awardedCount, overRequested, overQuoted };
}
